import { createFileRoute } from '@tanstack/react-router';
import { lazy } from 'react';
import { LazyComponent as LazyWrapper, RouteErrorComponent } from '@/components/ui';

const DutiesPage = lazy(() => import('../pages/duties-page'));

export const Route = createFileRoute('/_protected/substitute/marketplace')({
  loader: async () => {
    const { default: i18n } = await import('../i18n');
    await i18n.loadNamespaces?.(['duties', 'common']);
    return null;
  },
  errorComponent: RouteErrorComponent,
  component: () => (
    <LazyWrapper>
      <DutiesPage />
      <ApplyModal />
    </LazyWrapper>
  ),
});

// Lightweight apply modal mounted in this route so DutiesPage (or any child)
// can open it by dispatching a `window.dispatchEvent(new CustomEvent(`openApplyModal`, { detail: job }))`.
import React, { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, Info } from 'lucide-react';
import { type ApplyModalJob, type ApplyForJobEventDetail } from '@/types/duty';
import { type ChoosableApplicationValidityDays, CHOOSABLE_APPLICATION_VALIDITY_DAYS, DEFAULT_APPLICATION_VALIDITY_DAYS } from '@/types/validators';

const ApplyModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState<ApplyModalJob | null>(null);
  const [priceValue, setPriceValue] = useState(45);
  const [validityDays, setValidityDays] = useState<ChoosableApplicationValidityDays>(DEFAULT_APPLICATION_VALIDITY_DAYS);
  const [error, setError] = useState<string | null>(null);

  // DA-463: Query verification status to show info banner for unverified users
  const verificationStatus = useQuery(api.app.verification.getMyVerificationStatus, {});
  const isVerified = verificationStatus?.canApplyToDuties ?? true;

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ApplyModalJob>).detail;
      setJob(detail ?? null);
      // Set initial slider value to min if provided
      const initialValue = detail?.payment?.min ?? 45;
      setPriceValue(initialValue);
      setValidityDays(DEFAULT_APPLICATION_VALIDITY_DAYS);
      setError(null);
      setOpen(true);
    };

    window.addEventListener('openApplyModal', handler as EventListener);
    return () => window.removeEventListener('openApplyModal', handler as EventListener);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setJob(null);
  };

  if (!job && !open) return null;

  const min = job?.payment?.min ?? 45;
  const max = job?.payment?.max ?? 200;
  const isFixedPrice = job?.compensationType === 'fixed';

  const validateAndSubmit = () => {
    setError(null);

    // For fixed price, no validation needed
    if (!isFixedPrice) {
      if (priceValue < min) {
        setError(`Der eingegebene Preis liegt unter dem Minimum von ${min}€`);
        return;
      }
      if (priceValue > max) {
        setError(`Der eingegebene Preis liegt über dem Maximum von ${max}€`);
        return;
      }
    }

    // Normalize job id (support _id or id) then dispatch
    const jobId = job?.id ?? job?._id ?? null;
    const eventDetail: ApplyForJobEventDetail = {
      jobId: jobId as string,
      proposedCompensation: isFixedPrice ? min : priceValue,
      validityDays, // DA-434: Include validity period
      isVerified, // DA-463: Pass verification status for toast message
      serviceType: job?.serviceType,
      compensationType: job?.compensationType,
      source: 'marketplace',
    };
    window.dispatchEvent(new CustomEvent('applyForJob', { detail: eventDetail }));

    handleClose();
  };

  // Calculate duration for total price
  const startMs = job?.startDate ? new Date(job.startDate).getTime() : null;
  const endMs = job?.endDate ? new Date(job.endDate).getTime() : null;
  const hours = startMs && endMs && endMs > startMs ? (endMs - startMs) / 3600000 : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>Bewerbung für {job?.title ?? 'Dienst'}</DialogTitle>
          <DialogDescription className='sr-only'>Bewerbungsformular mit Preisvorschlag und Gültigkeitsdauer</DialogDescription>
        </DialogHeader>

        {/* DA-463: Show info banner for unverified users */}
        {!isVerified && (
          <Alert className='border-info/20 bg-info/5'>
            <Info className='h-4 w-4 text-info' />
            <AlertDescription className='text-info-foreground'>Ihre Bewerbung wird gespeichert und automatisch aktiviert, sobald Ihr Profil verifiziert wurde.</AlertDescription>
          </Alert>
        )}

        {isFixedPrice ? (
          /* Fixed pricing display */
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>Feste Vergütung</p>

            {/* Hourly rate */}
            <div className='p-4 rounded-lg bg-muted/30'>
              <div className='text-center'>
                <div className='text-3xl font-bold text-foreground mb-1'>{min}€ / Stunde</div>
                {hours && (
                  <div className='text-lg text-muted-foreground'>
                    Gesamtvergütung: {(min * hours).toFixed(2)}€ (für {hours.toFixed(2)} Std.)
                  </div>
                )}
              </div>
            </div>

            {/* Notfallscheinbeteiligung note */}
            {job?.hasNotfallscheinbeteiligung && job?.notfallscheinbeteiligungRate && (
              <div className='p-3 rounded-md bg-primary/5 border border-primary/20'>
                <div className='text-sm'>
                  <span className='font-medium text-primary'>+ {job.notfallscheinbeteiligungRate}€ pro Patient</span>
                  <span className='text-muted-foreground'> (Notfallscheinbeteiligung)</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Range pricing with slider */
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>
              {min != null && <span>Min: {min}€/Std. </span>}
              {max != null && <span>Max: {max}€/Std.</span>}
            </p>

            <label className='block text-sm font-medium'>Ihr Preisvorschlag (€/Stunde)</label>

            {/* Slider with gradient background */}
            <div className='w-full flex items-center justify-between gap-4'>
              <span className='text-sm text-muted-foreground'>{min} €</span>
              <div className='flex-1 relative'>
                {/* Gradient background for the slider */}
                <div
                  className='absolute inset-0 h-1.5 rounded-full pointer-events-none'
                  style={{
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background:
                      'linear-gradient(to right, ' +
                      'oklch(0.65 0.2 30) 0%, ' +
                      'oklch(0.68 0.18 35) 6%, ' +
                      'oklch(0.72 0.16 45) 16%, ' +
                      'oklch(0.75 0.14 55) 23%, ' +
                      'oklch(0.78 0.12 70) 29%, ' +
                      'oklch(0.80 0.11 90) 32%, ' +
                      'oklch(0.85 0.14 140) 35%, ' +
                      'oklch(0.88 0.16 148) 39%, ' +
                      'oklch(0.88 0.16 148) 42%, ' +
                      'oklch(0.85 0.14 145) 45%, ' +
                      'oklch(0.80 0.12 140) 52%, ' +
                      'oklch(0.75 0.09 135) 58%, ' +
                      'oklch(0.68 0.07 160) 68%, ' +
                      'oklch(0.62 0.05 180) 84%, ' +
                      'oklch(0.6 0.05 200) 100%)',
                  }}
                />
                <Slider value={[priceValue]} onValueChange={(values: number | readonly number[]) => setPriceValue(Array.isArray(values) ? values[0] : values)} min={min} max={max} step={5} />
              </div>
              <span className='text-sm text-muted-foreground'>{max} €</span>
            </div>

            {/* Current value display */}
            <p className='text-center text-lg font-semibold'>{priceValue} € / Stunde</p>

            {/* Warning for high prices (80% of max range) */}
            {priceValue > min + (max - min) * 0.8 && (
              <div className='p-3 rounded-md bg-warning/10 border border-warning/20' role='alert'>
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='size-5 text-warning shrink-0 mt-0.5' aria-hidden='true' />
                  <div className='text-sm text-warning'>Ihr Preis ist sehr hoch, es ist unwahrscheinlich, dass Sie zu diesem Preis ausgewählt werden</div>
                </div>
              </div>
            )}

            {/* Helper: calculate total based on service length */}
            {hours && <p className='text-sm text-muted-foreground text-center'>{`Gesamtvergütung: €${(priceValue * hours).toFixed(2)} (auf Basis von ${hours.toFixed(2)} Std.)`}</p>}

            {/* Notfallscheinbeteiligung note for range pricing */}
            {job?.hasNotfallscheinbeteiligung && job?.notfallscheinbeteiligungRate && (
              <div className='p-3 rounded-md bg-primary/5 border border-primary/20'>
                <div className='text-sm'>
                  <span className='font-medium text-primary'>+ {job.notfallscheinbeteiligungRate}€ pro Patient</span>
                  <span className='text-muted-foreground'> (Notfallscheinbeteiligung)</span>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className='text-sm text-destructive'>{error}</p>}

        {/* DA-434: Validity period selector */}
        <div className='border-t pt-4'>
          <label className='mb-2 block text-sm font-medium'>Gültigkeitsdauer</label>
          <div className='flex gap-2'>
            {CHOOSABLE_APPLICATION_VALIDITY_DAYS.map((days) => (
              <Button key={days} type='button' variant={validityDays === days ? 'default' : 'outline'} size='sm' className='flex-1' onClick={() => setValidityDays(days)}>
                {days} Tage
              </Button>
            ))}
          </div>
          <p className='mt-1.5 text-xs text-muted-foreground'>Wie lange soll Ihre Bewerbung gültig sein?</p>
        </div>

        <DialogFooter>
          <Button variant='ghost' onClick={handleClose}>
            Abbrechen
          </Button>
          <Button onClick={validateAndSubmit}>Bewerbung absenden</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyModal;
